import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IBackendCart, IItem, IServerOrder } from '@rbi-ctg/menu';
import Picture from 'components/picture';
import { DeliveryStatus } from 'generated/rbi-graphql';
import { useDonations } from 'hooks/use-donations';
import { DONATIONS } from 'hooks/use-donations/constants';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useOrderContext } from 'state/order';
import useDonation from 'state/order/hooks/use-donation';

import theme from '../theme';

import Donations from './donations';
import DonationsRow from './donations-row';
import GrandTotal from './grand-total';
import SubTotal from './subtotal';
import TaxesAndFees from './taxes-and-fees';
import TaxesAndFeesNextStep from './taxes-and-fees-next-step';
import TaxesRow from './taxes-row';
import TipSelector from './tip-selector';
import { useTotalTipCalculations } from './use-total-tip-calculations';

export interface ITotals {
  serverOrder: IServerOrder | null;
  serviceMode: ServiceMode | null;
  rewardApplied?: boolean;
}

export enum Size {
  SMALL = '108px',
  LARGE = '236px',
}

interface IWrapper {
  size?: Size;
}

const Wrapper = styled.div<IWrapper>`
  padding: ${theme.totalsPadding};
  /* to prevent the section from collapsing */
  /*
   * Large is for delivery
   * Small is none delivery
   */
  min-height: ${props => props.size};
`;

const Separator = styled.hr`
  margin: 1rem 0;
  border: none;
  border-bottom: 1px solid ${Styles.color.grey.three};
`;

const TotalsContainer = styled.div``;

const DeliveryLogoContainer = styled.div`
  width: 10rem;
  margin: 2rem auto;
`;

export const Totals: FC<ITotals> = ({ serverOrder, serviceMode }) => {
  const orderContext = useOrderContext();
  const {
    subTotalCents = 0,
    taxCents = 0,
    rewardDiscountAmount,
    serverOrderTip,
    additionalDiscountAmount,
    deliveryFee,
    deliveryFeeDiscount,
    cart,
    delivery,
    loading,
    isDeliveryOrder,
    isTwoStepCheckout,
    hideTipAmount,
  } = useTotalTipCalculations({ serverOrder, serviceMode });

  const { formatMessage } = useIntl();
  const { featureDisclaimers } = useFeatureDisclaimers();
  const { deliveryLogo } = featureDisclaimers || {};

  const { cartTotal = 0, isCartTotalNegative = false } =
    orderContext?.calculateCartTotalWithDiscount?.() || {};

  const {
    donationRowContent: roundUpDonationRowContent,
    donationRowModalContent: roundUpDonationModalContent,
  } = useDonations({
    donations: serverOrder?.cart?.donations,
    donationType: DONATIONS.ROUND_UP,
  });

  const enableTHRoundUpToggle = useFlag(LaunchDarklyFlag.ENABLE_TH_ROUND_UP);
  const enableHideTaxLine = useFlag(LaunchDarklyFlag.ENABLE_HIDE_TAX_LINE);
  const enableHideSubTotalLine = useFlag(LaunchDarklyFlag.ENABLE_HIDE_SUBTOTAL_LINE);
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const { featureDonation } = useFeatureDonation();
  const donationItems =
    featureDonation?.donationItemList?.map(item => ({ ...item } as IItem)) || [];
  const { selectedDonationCartEntry } = useDonation({
    donationItems,
  });

  const donationAmount = selectedDonationCartEntry?.price || 0;

  // If "SignUp After Cart" is enabled:
  // - Use cart preview values
  // Otherwise
  // - Use server cart
  const skipOrderPricing = isSignUpAfterCart || isCartTotalNegative;
  const cartSelected = skipOrderPricing
    ? ({
        totalCents: cartTotal,
        subTotalCents: cartTotal,
      } as IBackendCart)
    : cart;
  // tip is only included in grand total on payment screen during two step checkout
  const hideTipInTotal = isTwoStepCheckout;

  const isLoading = loading || !serverOrder || (isDeliveryOrder && !serverOrder.delivery);
  const deliveryLoading = serverOrder?.delivery?.status === DeliveryStatus.QUOTE_REQUESTED;
  const dropoffState = serverOrder?.delivery?.dropoff?.state;

  const otherDiscountAmount = isCartTotalNegative ? 0 : additionalDiscountAmount;
  const rewardDiscount = isCartTotalNegative ? 0 : rewardDiscountAmount;

  return (
    <Wrapper size={isDeliveryOrder ? Size.LARGE : Size.SMALL}>
      <TotalsContainer tabIndex={0}>
        {enableTHRoundUpToggle && !!roundUpDonationRowContent && (
          <DonationsRow
            isLoading={isLoading}
            totalCents={roundUpDonationRowContent.totalCents}
            message={roundUpDonationRowContent.message}
            modalContent={roundUpDonationModalContent}
            iconAriaLabel={roundUpDonationRowContent.iconAriaLabel}
          />
        )}
        {!enableHideSubTotalLine && (
          <SubTotal
            cart={cartSelected}
            isLoading={isLoading && !skipOrderPricing}
            otherDiscountAmount={otherDiscountAmount}
            rewardDiscount={rewardDiscount}
          />
        )}
        {skipOrderPricing ? (
          <TaxesAndFeesNextStep />
        ) : (
          <>
            {!enableHideTaxLine && !isDeliveryOrder && (
              <TaxesRow taxCents={taxCents} isLoading={isLoading} />
            )}

            {isDeliveryOrder && (
              <TaxesAndFees
                deliveryFee={deliveryFee}
                deliveryFeeDiscount={deliveryFeeDiscount}
                dropoffState={dropoffState}
                serviceFeeCents={delivery?.serviceFeeCents ?? 0}
                smallCartFeeCents={delivery?.smallCartFeeCents ?? 0}
                geographicalFeeCents={delivery?.geographicalFeeCents ?? 0}
                taxCents={taxCents}
                tipAmount={orderContext.tipAmount}
                serviceMode={serviceMode}
                subTotalCents={subTotalCents}
                isLoading={isLoading || deliveryLoading}
              />
            )}

            {isDeliveryOrder && !isTwoStepCheckout && !hideTipAmount && (
              <TipSelector
                isLoading={isLoading || deliveryLoading}
                subTotalCents={subTotalCents}
                otherDiscountAmount={additionalDiscountAmount}
                showTipPercentage={orderContext.shouldShowTipPercentage(
                  subTotalCents + additionalDiscountAmount
                )}
              />
            )}
          </>
        )}

        {donationAmount > 0 && (
          <Donations isLoading={isLoading && !skipOrderPricing} totalCents={donationAmount} />
        )}

        <Separator />

        <GrandTotal
          cart={cartSelected}
          isLoading={isLoading && !skipOrderPricing}
          serverOrderTip={serverOrderTip}
          tipAmount={hideTipInTotal ? 0 : orderContext.tipAmount}
        />
      </TotalsContainer>
      {isDeliveryOrder && deliveryLogo?.locale && (
        <DeliveryLogoContainer>
          <Picture image={deliveryLogo?.locale} alt={formatMessage({ id: 'deliveryLogoAlt' })} />
        </DeliveryLogoContainer>
      )}
    </Wrapper>
  );
};

export default Totals;
